<script lang="ts">
  import Copy from '@lucide/svelte/icons/copy';
  import FileJson from '@lucide/svelte/icons/file-json';
  import ImageDown from '@lucide/svelte/icons/image-down';
  import TriangleAlert from '@lucide/svelte/icons/triangle-alert';

  import { copyText, downloadBlob, downloadText } from '../../app/browser';
  import { getServices } from '../../app/context';
  import { JSON_FILE_NAME, JSON_MIME, serializeDoc } from '../../io/json-file';
  import { encodeLink } from '../../io/link';
  import { buildShareUrl, LONG_LINK_THRESHOLD } from '../../io/share-url';
  import { textSummary } from '../../io/text-summary';
  import type { ImageTheme } from '../../render/layout';
  import { format, strings } from '../../strings';
  import Button from '../../ui/Button.svelte';
  import Dialog from '../../ui/Dialog.svelte';

  let { open = $bindable(false) }: { open?: boolean } = $props();

  const { store, toasts, settings } = getServices();
  const id = $props.id();
  let input: HTMLInputElement | undefined = $state();
  let includeComments = $state(true);
  let imageTheme: ImageTheme = $state('light');
  let exporting = $state(false);

  $effect(() => {
    if (open) imageTheme = document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
  });

  const hasComments = $derived(
    store.doc.categories.some((c) => c.kinks.some((k) => k.comment !== undefined)),
  );
  // Only encode while the dialog is open.
  const link = $derived(
    open
      ? buildShareUrl(window.location, encodeLink(store.doc, { comments: includeComments }))
      : '',
  );

  async function copyLink() {
    if (await copyText(link)) {
      toasts.show(strings.linkCopied);
    } else {
      input?.focus();
      input?.select();
      toasts.show(strings.copyFailed);
    }
  }

  async function downloadImage() {
    exporting = true;
    try {
      // Loaded on demand: keeps the canvas code out of the initial bundle.
      const { exportImage, IMAGE_FILE_NAME } = await import('../../render/export');
      const blob = await exportImage(store.doc, {
        theme: imageTheme,
        footnotes: settings.value.imageFootnotes,
        embed: settings.value.embedData,
        site: `${window.location.host}${window.location.pathname}`.replace(/\/$/, ''),
        date: new Date().toLocaleDateString('en-CA'),
      });
      downloadBlob(blob, IMAGE_FILE_NAME);
    } catch {
      toasts.show(strings.imageFailed);
    } finally {
      exporting = false;
    }
  }

  async function copySummary() {
    toasts.show((await copyText(textSummary(store.doc))) ? strings.textCopied : strings.copyFailed);
  }
</script>

<Dialog bind:open title={strings.shareTitle} size="lg">
  <div class="flex flex-col divide-y divide-line">
    <section class="flex flex-col gap-2 pb-4">
      <label for="{id}-link" class="text-sm font-semibold">{strings.shareLinkLabel}</label>
      <div class="flex gap-2">
        <input
          bind:this={input}
          id="{id}-link"
          readonly
          value={link}
          aria-describedby="{id}-hint {id}-length"
          class="min-h-10 min-w-0 flex-1 rounded-lg border border-line-strong/70 bg-surface-2 px-3 font-mono text-sm text-ink"
          onfocus={(event) => {
            event.currentTarget.select();
          }}
        />
        <Button variant="primary" onclick={copyLink}>
          <Copy size={16} aria-hidden="true" />
          {strings.copyLink}
        </Button>
      </div>
      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
        <p id="{id}-length">{format.linkLength(link.length)}</p>
        {#if hasComments}
          <label class="flex items-center gap-1.5">
            <input type="checkbox" bind:checked={includeComments} class="size-4 accent-accent" />
            {strings.includeComments}
          </label>
        {/if}
      </div>
      {#if link.length > LONG_LINK_THRESHOLD}
        <p class="flex items-start gap-2 rounded-lg bg-danger-soft p-3 text-sm" role="status">
          <TriangleAlert size={16} aria-hidden="true" class="mt-0.5 shrink-0 text-danger" />
          {strings.longLinkWarning}
        </p>
      {/if}
      <p id="{id}-hint" class="text-sm text-ink-muted">{strings.shareLinkHint}</p>
    </section>

    <section class="flex flex-col gap-3 py-4">
      <div class="flex flex-col gap-0.5">
        <h3 class="text-sm font-semibold">{strings.imageSection}</h3>
        <p class="text-sm text-ink-muted">{strings.imageHint}</p>
      </div>
      <div class="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
        <fieldset class="flex items-center gap-3">
          <legend class="sr-only">{strings.imageTheme}</legend>
          <span aria-hidden="true" class="text-ink-muted">{strings.imageTheme}:</span>
          {#each [['light', strings.themeLight], ['dark', strings.themeDark]] as const as [value, label] (value)}
            <label class="flex items-center gap-1.5">
              <input
                type="radio"
                name="{id}-theme"
                {value}
                bind:group={imageTheme}
                class="size-4 accent-accent"
              />
              {label}
            </label>
          {/each}
        </fieldset>
        <label class="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={settings.value.imageFootnotes}
            class="size-4 accent-accent"
            onchange={(event) => {
              settings.update({ imageFootnotes: event.currentTarget.checked });
            }}
          />
          {strings.footnotes}
        </label>
        <label class="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={settings.value.embedData}
            aria-describedby="{id}-embed"
            class="size-4 accent-accent"
            onchange={(event) => {
              settings.update({ embedData: event.currentTarget.checked });
            }}
          />
          {strings.embedData}
        </label>
      </div>
      <p id="{id}-embed" class="text-xs text-ink-muted">{strings.embedHint}</p>
      <Button class="self-start" onclick={downloadImage} disabled={exporting}>
        <ImageDown size={16} aria-hidden="true" />
        {strings.downloadImage}
      </Button>
    </section>

    <section class="flex flex-col gap-3 py-4">
      <div class="flex flex-col gap-0.5">
        <h3 class="text-sm font-semibold">{strings.dataSection}</h3>
        <p class="text-sm text-ink-muted">{strings.dataHint}</p>
      </div>
      <Button
        class="self-start"
        onclick={() => {
          downloadText(serializeDoc(store.doc), JSON_FILE_NAME, JSON_MIME);
        }}
      >
        <FileJson size={16} aria-hidden="true" />
        {strings.downloadData}
      </Button>
    </section>

    <section class="flex flex-col gap-3 pt-4">
      <div class="flex flex-col gap-0.5">
        <h3 class="text-sm font-semibold">{strings.textSection}</h3>
        <p class="text-sm text-ink-muted">{strings.textHint}</p>
      </div>
      <Button class="self-start" onclick={copySummary}>
        <Copy size={16} aria-hidden="true" />
        {strings.copyText}
      </Button>
    </section>
  </div>
</Dialog>
